/**
 * Paso "Documentos" del perfil del agente: requisitos administrables desde
 * Sistema > Documentos requeridos (`documentos_requeridos` + `_tipos` + `_perfiles`).
 *
 * Espejo LITERAL de `_shared/onboarding_agente.ts` (sozu-edge-functions): el % del
 * portal web tiene que coincidir con el de la app de agentes y con el gate de
 * generar ofertas. Si cambias algo aquí, cámbialo allá.
 */

export interface RequisitoDocumento {
  clave: string;
  nombre: string;
  /** Alternativas: cualquiera cumple. INE frente (2) y reverso (3) cuentan juntas. */
  tipos: number[];
  exigeValidado: boolean;
  aplicaInterno: boolean;
  aplicaExterno: boolean;
}

export interface DocOnboarding {
  id_tipo_documento: number;
  id_estatus_verificacion: number | null;
}

const INE_FRENTE = 2;
const INE_REVERSO = 3;
const CARTA = 48;
/** estatus_verificacion: 1 pendiente, 2 validado, 3 rechazado, 4 expirado. */
const VALIDADO = 2;
const RECHAZADO = 3;
const EXPIRADO = 4;

/** Rol 3: Agente Inmobiliario externo. Rol 9: Agente Interno. */
const ROL_EXTERNO = 3;
const ROL_INTERNO = 9;

/**
 * ¿El agente es externo? Mismo criterio que el rol efectivo de la EF: su
 * `usuarios.rol_id` si es de agente; si no, sus roles adicionales prefiriendo el 3.
 */
export function esAgenteExterno(rolUsuario: number | null | undefined, rolesAdicionales: number[]): boolean {
  if (rolUsuario === ROL_EXTERNO || rolUsuario === ROL_INTERNO) return rolUsuario === ROL_EXTERNO;
  return rolesAdicionales.includes(ROL_EXTERNO);
}

/**
 * Fila de `documentos_requeridos` con sus tipos y perfiles embebidos -> requisito de
 * agente. Sin tipos, o sin perfil de agente (es de clientes), se descarta.
 */
export function requisitoDesdeFila(r: Record<string, unknown>): RequisitoDocumento | null {
  // Tablas hijas normalizadas: un renglón por tipo (FK a tipos_documento) y por perfil.
  const hijos = (r.documentos_requeridos_tipos ?? []) as { id_tipo_documento: unknown }[];
  const tipos = hijos.map((t) => Number(t.id_tipo_documento)).filter(Number.isInteger);
  const perfiles = ((r.documentos_requeridos_perfiles ?? []) as { perfil: unknown }[]).map((p) => String(p.perfil));
  const aplicaInterno = perfiles.includes('agente_interno');
  const aplicaExterno = perfiles.includes('agente_externo');
  if (!tipos.length || !(aplicaInterno || aplicaExterno)) return null;
  return {
    clave: String(r.clave ?? ''),
    nombre: String(r.nombre ?? ''),
    tipos,
    exigeValidado: r.exige_validado !== false,
    aplicaInterno,
    aplicaExterno,
  };
}

export function requisitosQueAplican(requisitos: RequisitoDocumento[], esExterno: boolean): RequisitoDocumento[] {
  return requisitos.filter((r) => (esExterno ? r.aplicaExterno : r.aplicaInterno));
}

/** La INE frente+reverso es UNA alternativa; el resto, una por tipo. */
function alternativas(tipos: number[]): number[][] {
  const out: number[][] = [];
  if (tipos.includes(INE_FRENTE) || tipos.includes(INE_REVERSO)) out.push([INE_FRENTE, INE_REVERSO]);
  for (const t of tipos) if (t !== INE_FRENTE && t !== INE_REVERSO) out.push([t]);
  return out;
}

/** Un requisito contra los documentos. La carta (48) también se cumple con la firma completada. */
export function evaluarRequisito(
  r: RequisitoDocumento,
  documentos: DocOnboarding[],
  cartaFirmada: boolean,
): { completo: boolean; parcial: boolean; faltante: string | null } {
  const cumpleTipo = (t: number) =>
    (t === CARTA && cartaFirmada) ||
    documentos.some(
      (d) => d.id_tipo_documento === t && (!r.exigeValidado || d.id_estatus_verificacion === VALIDADO),
    );
  if (alternativas(r.tipos).some((alt) => alt.every(cumpleTipo))) {
    return { completo: true, parcial: false, faltante: null };
  }
  const suyos = documentos.filter((d) => r.tipos.includes(d.id_tipo_documento));
  if (!suyos.length) return { completo: false, parcial: false, faltante: r.nombre };
  const estado = suyos.some((d) => d.id_estatus_verificacion === RECHAZADO)
    ? 'rechazado'
    : suyos.some((d) => d.id_estatus_verificacion === EXPIRADO)
      ? 'expirado'
      : 'pendiente de validación';
  return { completo: false, parcial: true, faltante: `${r.nombre} (${estado})` };
}

/** Paso "Documentos", o null si no hay requisitos que apliquen (el paso no existe). */
export function pasoDocumentos(
  requisitos: RequisitoDocumento[],
  documentos: DocOnboarding[],
  cartaFirmada: boolean,
): { completo: boolean; parcial: boolean; faltantes: string[] } | null {
  if (!requisitos.length) return null;
  const evaluados = requisitos.map((r) => evaluarRequisito(r, documentos, cartaFirmada));
  const completo = evaluados.every((e) => e.completo);
  return {
    completo,
    parcial: !completo && evaluados.some((e) => e.completo || e.parcial),
    faltantes: evaluados.map((e) => e.faltante).filter((f): f is string => f !== null),
  };
}
